import React, { createContext, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from './AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/';

export const DataPulseContext = createContext();

export const DataPulseProvider = ({ children }) => {
  const { token } = useContext(AuthContext);
  
  const [surveys, setSurveys] = useState([]);
  const [currentSurvey, setCurrentSurvey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Load all surveys with statistics
   */
  const loadSurveys = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}datapulse/surveys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurveys(response.data);
      return response.data;
    } catch (error) {
      console.error('Error loading surveys:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load a single survey with all questions and options
   */
  const loadSurvey = async (surveyId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}datapulse/surveys/${surveyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSurvey(response.data);
      return response.data;
    } catch (error) {
      console.error('Error loading survey:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Create a new survey
   */
  const createSurvey = async (surveyData) => {
    try {
      const response = await axios.post(`${API_URL}datapulse/surveys`, surveyData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadSurveys(); // Refresh list
      return response.data;
    } catch (error) {
      console.error('Error creating survey:', error);
      throw error;
    }
  };

  /**
   * Update an existing survey
   */
  const updateSurvey = async (surveyId, surveyData) => {
    try {
      const response = await axios.put(`${API_URL}datapulse/surveys/${surveyId}`, surveyData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadSurveys(); // Refresh list
      return response.data;
    } catch (error) {
      console.error('Error updating survey:', error);
      throw error;
    }
  };

  /**
   * Get campaigns associated with a survey
   */
  const getSurveyCampaigns = async (surveyId) => {
    try {
      const response = await axios.get(`${API_URL}datapulse/surveys/${surveyId}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching survey campaigns:', error);
      throw error;
    }
  };

  /**
   * Duplicate a survey
   */
  const duplicateSurvey = async (surveyId) => {
    try {
      const response = await axios.post(`${API_URL}datapulse/surveys/${surveyId}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadSurveys(); // Refresh list
      return response.data;
    } catch (error) {
      console.error('Error duplicating survey:', error);
      throw error;
    }
  };

  /**
   * Delete a survey
   */
  const deleteSurvey = async (surveyId) => {
    try {
      const response = await axios.delete(`${API_URL}datapulse/surveys/${surveyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadSurveys(); // Refresh list
      return response.data;
    } catch (error) {
      console.error('Error deleting survey:', error);
      throw error;
    }
  };

  /**
   * Add a question to a survey
   */
  const addQuestion = async (surveyId, questionData) => {
    try {
      const response = await axios.post(`${API_URL}datapulse/surveys/${surveyId}/questions`, questionData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error adding question:', error);
      throw error;
    }
  };

  /**
   * Update a question
   */
  const updateQuestion = async (questionId, questionData) => {
    try {
      const response = await axios.put(`${API_URL}datapulse/questions/${questionId}`, questionData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  };

  /**
   * Check if questions are locked (have answers)
   * Returns array of { question_id, answer_count, locked }
   */
  const checkQuestionLocks = async (surveyId) => {
    try {
      const response = await axios.get(`${API_URL}datapulse/surveys/${surveyId}/question-locks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error checking question locks:', error);
      return []; // Return empty array on error so UI doesn't break
    }
  };

  /**
   * Delete a question
   */
  const deleteQuestion = async (questionId) => {
    try {
      const response = await axios.delete(`${API_URL}datapulse/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  };

  /**
   * Send survey to recipients
   */
  const sendSurvey = async (surveyId, recipientData) => {
    try {
      const response = await axios.post(`${API_URL}datapulse/surveys/${surveyId}/send`, recipientData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error sending survey:', error);
      throw error;
    }
  };

  /**
   * Get survey recipients
   */
  const getSurveyRecipients = async (surveyId) => {
    try {
      const response = await axios.get(`${API_URL}datapulse/surveys/${surveyId}/recipients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching recipients:', error);
      throw error;
    }
  };

  /**
   * Get survey analytics
   */
  const getSurveyAnalytics = async (surveyId) => {
    try {
      const response = await axios.get(`${API_URL}datapulse/surveys/${surveyId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  };

  /**
   * Get survey responses
   */
  const getSurveyResponses = async (surveyId) => {
    try {
      const response = await axios.get(`${API_URL}datapulse/surveys/${surveyId}/responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching responses:', error);
      throw error;
    }
  };

  /**
   * Delete a survey response
   */
  const deleteResponse = async (surveyId, responseId) => {
    try {
      await axios.delete(`${API_URL}datapulse/surveys/${surveyId}/responses/${responseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error deleting response:', error);
      throw error;
    }
  };

  /**
   * Get static HTML preview for a saved survey (iframe src)
   */
  const getSurveyPreviewHtmlUrl = (surveyId) => {
    if (!surveyId || !token) return null;
    // Pass token via query (backend accepts ?auth=Bearer <token>)
    return `${API_URL}datapulse/surveys/${surveyId}/preview?auth=${encodeURIComponent(`Bearer ${token}`)}`;
  };

  /**
   * Build live preview HTML (client-side render future: will use POST endpoint once implemented)
   * Placeholder for dynamic srcDoc usage – for now we just mirror static until POST route added.
   */
  const buildLivePreviewHtml = (surveyData, questionsData) => {
    if (!surveyData) return '<p>No survey data</p>';
    const esc = (v) => (v || '').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const questionsHTML = (questionsData || []).map((q, idx) => {
      const fieldName = `q_${idx + 1}`;
      const label = `${idx + 1}. ${esc(q.question_text || 'Untitled question')}${q.is_required ? ' <span style="color:#b71c1c">*</span>' : ''}`;
      let input = '';
      const opts = q.options || [];
      switch (q.question_type) {
        case 'text':
          input = `<input type="text" name="${fieldName}" placeholder="${esc(q.placeholder || '')}" style="width:100%;padding:10px;border:1px solid #ddd;border-radius:6px;font-size:14px" />`; break;
        case 'email':
          input = `<input type="email" name="${fieldName}" placeholder="${esc(q.placeholder || 'email@example.com')}" style="width:100%;padding:10px;border:1px solid #ddd;border-radius:6px;font-size:14px" />`; break;
        case 'phone':
          input = `<input type="tel" name="${fieldName}" placeholder="${esc(q.placeholder || '(555) 123-4567')}" style="width:100%;padding:10px;border:1px solid #ddd;border-radius:6px;font-size:14px" />`; break;
        case 'textarea':
          input = `<textarea name="${fieldName}" rows="4" placeholder="${esc(q.placeholder || '')}" style="width:100%;padding:10px;border:1px solid #ddd;border-radius:6px;font-size:14px;resize:vertical"></textarea>`; break;
        case 'multiple_choice':
          input = opts.map((o, i) => `<label class="radio-option"><input type="radio" name="${fieldName}" value="${esc(o.option_text)}" /><span class="radio-custom"></span><span class="option-text">${esc(o.option_text)}</span></label>`).join(''); break;
        case 'checkboxes':
          input = opts.map((o, i) => `<label class="checkbox-option"><input type="checkbox" name="${fieldName}[]" value="${esc(o.option_text)}" /><span class="checkbox-custom"></span><span class="option-text">${esc(o.option_text)}</span></label>`).join(''); break;
        case 'dropdown':
          input = `<select name="${fieldName}" style="width:100%;padding:10px;border:1px solid #ddd;border-radius:6px;font-size:14px;background:#fff"><option value="">-- Select an option --</option>${opts.map(o => `<option value="${esc(o.option_text)}">${esc(o.option_text)}</option>`).join('')}</select>`; break;
        case 'rating': {
          const max = q.rating_scale || 5;
          input = `<div class="rating-container">${Array.from({length:max}, (_,i) => `<label class="rating-option"><input type="radio" name="${fieldName}" value="${i+1}" /><span class="rating-number">${i+1}</span></label>`).join('')}</div>`;
          break; }
        case 'yes_no':
          input = ['Yes','No'].map(v => `<label class="radio-option"><input type="radio" name="${fieldName}" value="${v}" /><span class="radio-custom"></span><span class="option-text">${v}</span></label>`).join(''); break;
        case 'date':
          input = `<input type="date" name="${fieldName}" style="width:100%;padding:10px;border:1px solid #ddd;border-radius:6px;font-size:14px" />`; break;
        default:
          input = `<input type="text" name="${fieldName}" style="width:100%;padding:10px;border:1px solid #ddd;border-radius:6px;font-size:14px" />`;
      }
      const helpTextHtml = q.help_text ? `<p style="margin:0 0 10px;color:#757575;font-size:13px;font-style:italic;line-height:1.5">${esc(q.help_text)}</p>` : '';
      return `<div style="margin-bottom:26px;padding-bottom:24px;border-bottom:1px solid #eee"><div style="font-size:15px;font-weight:500;color:#222;margin-bottom:12px">${label}</div>${helpTextHtml}${input}</div>`; 
    }).join('');
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src https://pcs-cdn.nyc3.cdn.digitaloceanspaces.com data:; style-src 'unsafe-inline';"><title>${esc(surveyData.name||'Preview')}</title><style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#f5f5f5;margin:0;padding:24px;} .container{max-width:860px;margin:0 auto;background:#fff;border-radius:10px;box-shadow:0 4px 18px rgba(0,0,0,0.08);overflow:hidden;} .header{padding:42px 42px 34px;border-bottom:1px solid #e0e0e0;text-align:center;background:#fff;} .logo{width:210px;margin:0 auto 30px;display:block;} .title{font-size:30px;font-weight:600;margin:0 0 10px;color:#212121;letter-spacing:-0.5px;} .desc{font-size:15px;color:#616161;max-width:760px;margin:0 auto;} .content{padding:42px;} .welcome{background:#f5f5f5;padding:18px 22px;border-left:4px solid #b71c1c;border-radius:6px;font-size:14px;color:#424242;margin-bottom:34px;} .footer{text-align:center;padding:18px;font-size:12px;color:#9e9e9e;background:#fafafa;border-top:1px solid #e0e0e0;} .radio-option,.checkbox-option{display:flex;align-items:center;margin-bottom:8px;padding:8px;border-radius:4px;cursor:pointer;position:relative;} .radio-option:hover,.checkbox-option:hover{background:#f5f5f5;} .radio-option input,.checkbox-option input{position:absolute;opacity:0;cursor:pointer;} .radio-custom,.checkbox-custom{width:18px;height:18px;border:2px solid #bbb;border-radius:50%;margin-right:10px;position:relative;flex-shrink:0;} .checkbox-custom{border-radius:4px;} .radio-option input:checked ~ .radio-custom{border-color:#b71c1c;background:#b71c1c;} .radio-option input:checked ~ .radio-custom::after{content:'';position:absolute;top:3px;left:3px;width:8px;height:8px;border-radius:50%;background:white;} .checkbox-option input:checked ~ .checkbox-custom{border-color:#b71c1c;background:#b71c1c;} .checkbox-option input:checked ~ .checkbox-custom::after{content:'✓';position:absolute;top:-2px;left:2px;color:white;font-size:14px;font-weight:bold;} .option-text{font-size:14px;color:#424242;} .rating-container{display:flex;gap:8px;flex-wrap:wrap;} .rating-option{position:relative;cursor:pointer;} .rating-option input{position:absolute;opacity:0;pointer-events:none;} .rating-number{display:flex;align-items:center;justify-content:center;width:44px;height:44px;border:2px solid #e0e0e0;border-radius:8px;font-size:15px;font-weight:600;color:#757575;background:#fafafa;transition:all 0.2s;} .rating-option:hover .rating-number{border-color:#b71c1c;background:#fff;} .rating-option input:checked ~ .rating-number{border-color:#b71c1c;background:#b71c1c;color:#fff;} .submit-btn{display:inline-block;background:#b71c1c;color:white;padding:12px 32px;border:none;border-radius:6px;font-size:15px;font-weight:600;cursor:not-allowed;margin-top:20px;opacity:0.7;} @media (max-width:880px){.container{max-width:100%;} .content{padding:32px;} .header{padding:36px 32px 28px;} .title{font-size:26px;} .logo{width:180px;margin-bottom:24px;} .rating-number{width:40px;height:40px;font-size:14px;} }</style></head><body><div class="container"><div class="header"><img class="logo" src="https://pcs-cdn.nyc3.cdn.digitaloceanspaces.com/ecs/assets/ecs-logo-c-f.png" alt="PCS Inc" /><h1 class="title">${esc(surveyData.name || 'Survey')}</h1>${surveyData.description?`<p class="desc">${esc(surveyData.description)}</p>`:''}</div><form><div class="content">${surveyData.welcome_message?`<div class="welcome">${esc(surveyData.welcome_message)}</div>`:''}${questionsHTML}<button type="button" class="submit-btn" disabled>Submit (Preview Only)</button></div></form><div class="footer">Interactive preview • Click inputs to see selection behavior</div></div></body></html>`;
  };

  /**
   * Server-rendered live preview for current (unsaved) edits
   * Returns HTML string rendered by backend (exact header/logo/styles)
   */
  const postSurveyLivePreview = async (surveyData, questionsData) => {
    try {
      const payload = {
        survey: surveyData || {},
        // Map placeholder -> placeholder_text for backend consistency
        questions: (questionsData || []).map(q => ({
          ...q,
          placeholder_text: q.placeholder ?? q.placeholder_text ?? '',
        }))
      };
      const res = await axios.post(`${API_URL}datapulse/surveys/preview`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        // Accept text/html back
        transformResponse: [(data) => data]
      });
      return res.data;
    } catch (error) {
      console.error('Error fetching live preview HTML:', error);
      throw error;
    }
  };

  // Note: Public survey rendering is now handled by backend HTML at services.pcsinconline.com/survey/:token
  // The loadPublicSurvey and submitSurveyResponse functions have been removed as they're no longer needed

  /**
   * Fetch survey preview HTML (for iframe srcDoc)
   */
  const fetchSurveyPreviewHtml = async (surveyId) => {
    try {
      const response = await axios.get(`${API_URL}datapulse/surveys/${surveyId}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching preview HTML:', error);
      throw error;
    }
  };

  const contextValue = {
    surveys,
    currentSurvey,
    isLoading,
    loadSurveys,
    loadSurvey,
    createSurvey,
    updateSurvey,
    duplicateSurvey,
    deleteSurvey,
    getSurveyCampaigns,
    addQuestion,
    updateQuestion,
    checkQuestionLocks,
    deleteQuestion,
    sendSurvey,
    getSurveyRecipients,
    getSurveyAnalytics,
    getSurveyResponses,
    deleteResponse,
    getSurveyPreviewHtmlUrl,
    buildLivePreviewHtml,
    postSurveyLivePreview,
    fetchSurveyPreviewHtml,
    setCurrentSurvey
  };

  return (
    <DataPulseContext.Provider value={contextValue}>
      {children}
    </DataPulseContext.Provider>
  );
};
